import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/dateUtils'
import { loginAuditApi, type LoginAuditRow } from '@/features/security/loginAuditApi'

/**
 * Admin → Logs.
 *
 * Andrew, 2026-09-24: "It needs a new screen in admin called logs. They will be more than the login
 * logs so it needs to select this and then display the logs with the functionality to search for
 * specific statuses and between certain dates and for a specific user ID."
 *
 * So the first control picks WHICH log, and the filters under it belong to that log. Sign-ins are
 * the only log today. Adding another means an entry in LOGS and a component that renders it with
 * the same shared controls: status, a date range and a user.
 *
 * Login Activity (People) is unchanged: it is the summary of sign-ins, with the charts and the PDF.
 * This is the search.
 */

const LOGS = [{ id: 'sign-ins', labelKey: 'logs.types.signIns', fallback: 'Sign-ins' }] as const
type LogId = (typeof LOGS)[number]['id']

const PAGE_SIZE = 50

/** The stored reason codes, in the order a person would look for them. */
const FAILURE_REASONS = [
    'bad_password', 'unknown_user', 'account_inactive', 'no_password_set',
    'mfa_invalid', 'mfa_required', 'passkey_invalid', 'rate_limited', 'server_error',
] as const

const REASON_FALLBACK: Record<string, string> = {
    bad_password: 'Wrong password',
    unknown_user: 'No such account',
    account_inactive: 'Account disabled',
    no_password_set: 'No password set',
    mfa_invalid: 'Wrong MFA code',
    mfa_required: 'MFA not completed',
    passkey_invalid: 'Passkey rejected',
    rate_limited: 'Too many attempts',
    server_error: 'Server error',
}

const EVENT_FALLBACK: Record<string, string> = {
    password_attempt: 'Password',
    mfa_attempt: 'MFA code',
    passkey_attempt: 'Passkey',
    logout: 'Sign out',
    lockout: 'Rate limited',
}

/** yyyy-mm-dd for a date input, in the viewer's own time zone. */
function dayInput(d: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * The chosen days as an instant range: the START of the first day and the END of the last, both in
 * the viewer's time zone, so "24 September to 24 September" means that whole day where they are.
 */
function dayRange(from: string, to: string): { from?: string; to?: string } {
    return {
        from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
        to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
    }
}

export default function LogsPage() {
    const { t } = useTranslation()
    const [log, setLog] = useState<LogId>('sign-ins')

    return (
        <div className="space-y-4 p-6">
            <div>
                <h1 className="text-2xl font-semibold">{t('logs.title', 'Logs')}</h1>
                <p className="text-sm text-muted-foreground">
                    {t('logs.subtitle', 'Search the records the application keeps. Choose a log, then narrow it by status, dates and user.')}
                </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
                <div className="space-y-1">
                    <Label htmlFor="log-type" className="text-xs">{t('logs.logLabel', 'Log')}</Label>
                    <Select value={log} onValueChange={(v) => setLog(v as LogId)}>
                        <SelectTrigger id="log-type" className="h-8 w-48 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {LOGS.map((l) => (
                                <SelectItem key={l.id} value={l.id}>{t(l.labelKey, l.fallback)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {log === 'sign-ins' && <SignInLog />}
        </div>
    )
}

/** Sign-ins: every attempt to sign in or to pass the second factor, from login_audit. */
function SignInLog() {
    const { t } = useTranslation()
    const today = new Date()
    const weekAgo = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000)

    // 'all' | 'success' | 'failure' | 'reason:<code>'
    const [status, setStatus] = useState('all')
    const [from, setFrom] = useState(dayInput(weekAgo))
    const [to, setTo] = useState(dayInput(today))
    const [userText, setUserText] = useState('')
    const [user, setUser] = useState('')
    const [page, setPage] = useState(0)

    const rangeBackwards = !!from && !!to && from > to
    const reset = () => setPage(0)
    const applyUser = () => { setUser(userText.trim()); reset() }
    const clearAll = () => {
        setStatus('all'); setFrom(dayInput(weekAgo)); setTo(dayInput(today)); setUserText(''); setUser(''); reset()
    }

    const filters = {
        ...dayRange(from, to),
        outcome: status === 'success' || status === 'failure' ? status : undefined,
        failureReason: status.startsWith('reason:') ? status.slice('reason:'.length) : undefined,
        user: user || undefined,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
    } as const

    const list = useQuery({
        queryKey: ['logs', 'sign-ins', filters],
        queryFn: () => loginAuditApi.list(filters),
        enabled: !rangeBackwards,
        placeholderData: (previous) => previous,
    })

    const rows = list.data?.rows ?? []
    const total = list.data?.total ?? 0
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
    const reason = (code: string | null) => (code ? t(`logs.reasons.${code}`, REASON_FALLBACK[code] ?? code) : '')

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-3 rounded-lg border bg-muted/20 p-3">
                <div className="space-y-1">
                    <Label htmlFor="log-status" className="text-xs">{t('logs.statusLabel', 'Status')}</Label>
                    <Select value={status} onValueChange={(v) => { setStatus(v); reset() }}>
                        <SelectTrigger id="log-status" className="h-8 w-52 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">{t('logs.status.all', 'Any status')}</SelectItem>
                            <SelectItem value="success">{t('logs.status.success', 'Succeeded')}</SelectItem>
                            <SelectItem value="failure">{t('logs.status.failure', 'Failed (any reason)')}</SelectItem>
                            <SelectGroup>
                                <SelectLabel className="text-xs">{t('logs.status.failedBecause', 'Failed because')}</SelectLabel>
                                {FAILURE_REASONS.map((code) => (
                                    <SelectItem key={code} value={`reason:${code}`}>{reason(code)}</SelectItem>
                                ))}
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-1">
                    <Label htmlFor="log-from" className="text-xs">{t('logs.fromLabel', 'From')}</Label>
                    <Input id="log-from" type="date" value={from} max={to || undefined}
                           onChange={(e) => { setFrom(e.target.value); reset() }} className="h-8 w-40 text-sm" />
                </div>
                <div className="space-y-1">
                    <Label htmlFor="log-to" className="text-xs">{t('logs.toLabel', 'To')}</Label>
                    <Input id="log-to" type="date" value={to} min={from || undefined}
                           onChange={(e) => { setTo(e.target.value); reset() }} className="h-8 w-40 text-sm" />
                </div>

                <div className="space-y-1">
                    <Label htmlFor="log-user" className="text-xs">{t('logs.userLabel', 'User')}</Label>
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            id="log-user"
                            value={userText}
                            onChange={(e) => setUserText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') applyUser() }}
                            onBlur={applyUser}
                            placeholder={t('logs.userPlaceholder', 'User ID or email')}
                            className="h-8 w-56 pl-7 text-sm"
                        />
                    </div>
                </div>

                <Button variant="ghost" size="sm" className="h-8" onClick={clearAll}>
                    <X className="mr-1 h-3.5 w-3.5" />{t('logs.clearFilters', 'Clear filters')}
                </Button>
            </div>

            {rangeBackwards && (
                <p role="alert" className="text-sm text-destructive">
                    {t('logs.rangeBackwards', 'The From date is after the To date.')}
                </p>
            )}

            <div className="overflow-x-auto rounded-xl border">
                <table className="w-full min-w-[900px] text-sm" data-testid="sign-in-log">
                    <thead>
                        <tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                            <th className="px-3 py-2 font-medium">{t('logs.cols.when', 'When')}</th>
                            <th className="px-3 py-2 font-medium">{t('logs.cols.user', 'User')}</th>
                            <th className="px-3 py-2 font-medium">{t('logs.cols.step', 'Step')}</th>
                            <th className="px-3 py-2 font-medium">{t('logs.cols.status', 'Status')}</th>
                            <th className="px-3 py-2 font-medium">{t('logs.cols.from', 'From')}</th>
                            <th className="px-3 py-2 font-medium">{t('logs.cols.device', 'Device')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.isLoading && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                                {t('common.loading', 'Loading…')}
                            </td></tr>
                        )}
                        {!list.isLoading && !rangeBackwards && rows.length === 0 && (
                            <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
                                {t('logs.empty', 'Nothing in this log matches these filters.')}
                            </td></tr>
                        )}
                        {rows.map((r: LoginAuditRow) => (
                            <tr key={r.id} className="border-b last:border-0 align-top hover:bg-muted/30">
                                <td className="whitespace-nowrap px-3 py-2 text-xs tabular-nums">{formatDateTime(r.occurredAt)}</td>
                                <td className="px-3 py-2">
                                    <div className="font-medium">{r.username ?? '—'}</div>
                                    <div className="text-xs text-muted-foreground tabular-nums">
                                        {r.userId != null
                                            ? t('logs.userId', 'User ID {{id}}', { id: r.userId })
                                            : t('logs.noAccount', 'No such account')}
                                    </div>
                                </td>
                                <td className="px-3 py-2 text-xs">{t(`logs.events.${r.event}`, EVENT_FALLBACK[r.event] ?? r.event)}</td>
                                <td className="px-3 py-2">
                                    <span className={cn(
                                        'rounded px-1.5 py-0.5 text-[11px]',
                                        r.outcome === 'success'
                                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300'
                                            : 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300',
                                    )}>
                                        {r.outcome === 'success'
                                            ? t('logs.status.success', 'Succeeded')
                                            : reason(r.failureReason) || t('logs.status.failed', 'Failed')}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    <div className="tabular-nums">{r.ip ?? '—'}</div>
                                    {(r.city || r.country) && (
                                        <div className="text-muted-foreground">{[r.city, r.region, r.country].filter(Boolean).join(', ')}</div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs">
                                    <div>{[r.browser, r.browserVersion].filter(Boolean).join(' ') || '—'}</div>
                                    <div className="text-muted-foreground">{[r.os, r.deviceType].filter(Boolean).join(' · ')}</div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span data-testid="sign-in-log-total">
                    {t('logs.pageOf', 'Page {{page}} of {{pages}} · {{total}} entries', { page: page + 1, pages, total })}
                </span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                            disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
                        {t('common.previous', 'Previous')}
                    </Button>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs"
                            disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
                        {t('common.next', 'Next')}
                    </Button>
                </div>
            </div>
        </div>
    )
}
